// MissionTaskChecklistEditor.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';

// Utilities
import {
  createEnumOptionId,
  generateEnumOptionValue,
} from 'utilities/EnumOptions';
import { normalizeChecklistTemplateItems } from 'utilities/ChecklistTemplateItems';
import {
  buildChecklistItemResource,
  normalizeChecklistItemResources,
} from 'utilities/ChecklistItemResources';
import { normalizeTypeKeyList } from 'utilities/nodeTypeRelatedLists';
import {
  buildSetFieldChecklistAction,
  buildTaskAttachChecklistAction,
  CHECKLIST_ITEM_ACTION_TYPES,
  getChecklistActionButtonLabel,
  getChecklistActionIconKey,
  getChecklistActionModalFields,
  getChecklistTaskAttachActionMenuOptions,
  normalizeChecklistItemActions,
} from 'missions/taskmodal/checklistItemActions';

// Components
import AddModal from 'components/addmodal/AddModal';
import Button from 'components/button/Button';
import Checkbox from 'components/checkbox/Checkbox';
import ContextMenu from 'components/contextmenu/ContextMenu';
import ModalField from 'components/modal/field/ModalField';
import TextInput from 'components/textinput/TextInput';
import Icon from 'svg/Icon';
import MissionTaskChecklistAddLinkModal from 'missions/taskmodal/MissionTaskChecklistAddLinkModal';
import MissionTaskChecklistLinkModal, {
  CHECKLIST_LINK_TYPE_KEY,
} from 'missions/taskmodal/MissionTaskChecklistLinkModal';
import MissionTaskChecklistSetFieldModal from 'missions/taskmodal/MissionTaskChecklistSetFieldModal';

const TASK_TYPE_KEY = 'TASK';

/**
 * Editor-facing label only — never fall back to the system key ("item", "item_2").
 *
 * @param {object|null|undefined} item
 * @returns {string}
 */
function getEditorItemLabel(item) {
  if (!item || typeof item !== 'object') return '';
  return String(item?.translations?.en?.label ?? item?.label ?? item?.text ?? '').trim();
}

/**
 * Keep stable keys/ids while allowing blank labels while the user is typing.
 *
 * @param {unknown} items
 * @returns {Array<object>}
 */
function normalizeEditorChecklistItems(items) {
  const source = Array.isArray(items) ? items : [];
  const rawById = new Map();
  source.forEach((raw) => {
    if (!raw || typeof raw !== 'object') return;
    const id = String(raw.id || '').trim();
    if (id) rawById.set(id, raw);
  });

  // Normalize first for stable keys/ids, then restore blank labels the shared
  // helper would otherwise fill from the system key ("item", "item_2").
  const normalized = normalizeChecklistTemplateItems(source);

  return normalized.map((item, index) => {
    const raw = rawById.get(String(item?.id || '').trim()) || source[index];
    const nextLabel = raw && typeof raw === 'object'
      ? getEditorItemLabel(raw)
      : getEditorItemLabel(item);
    const actions = normalizeChecklistItemActions(
      (raw && typeof raw === 'object' && Array.isArray(raw.actions))
        ? raw.actions
        : item?.actions,
    );
    const resources = normalizeChecklistItemResources(
      (raw && typeof raw === 'object' && Array.isArray(raw.resources))
        ? raw.resources
        : item?.resources,
    );

    return {
      ...item,
      translations: { en: { label: nextLabel } },
      sortOrder: index,
      ...(actions.length ? { actions } : { actions: [] }),
      ...(resources.length ? { resources } : { resources: [] }),
    };
  });
}

/**
 * MissionTaskChecklistEditor
 *
 * Authoring UI for a workflow step Task Checklist (not the Checklist Field type).
 *
 * @param {Array<object>} items - Normalized recipe items.
 * @param {boolean} required - Whether Complete is gated on recipe completion.
 * @param {function} onChange - ({ items, required }) => void
 * @param {object} theme
 * @param {string} [error]
 * @param {array} [availableFields] - Host-record schema fields for Set Field actions.
 * @returns {ReactNode}
 */
const MissionTaskChecklistEditor = React.memo(({
  items = [],
  required = false,
  onChange,
  theme,
  error = '',
  availableFields = [],
}) => {
  const { getType } = useNodeTypes();
  const checklistItems = useMemo(
    () => normalizeEditorChecklistItems(items),
    [items],
  );
  const modalFields = useMemo(
    () => getChecklistActionModalFields(availableFields),
    [availableFields],
  );
  const actionButtonOptions = useMemo(
    () => getChecklistTaskAttachActionMenuOptions(),
    [],
  );
  const taskRelatedTypeKeys = useMemo(() => {
    const configured = normalizeTypeKeyList(
      getType(TASK_TYPE_KEY)?.suggestedChildren,
    );
    return configured.length > 0 ? new Set(configured) : null;
  }, [getType]);
  const canUseLinkType = !taskRelatedTypeKeys
    || taskRelatedTypeKeys.has(CHECKLIST_LINK_TYPE_KEY);
  const [itemLabelDrafts, setItemLabelDrafts] = useState({});
  const [draggingItemId, setDraggingItemId] = useState('');
  const [dragOverItemId, setDragOverItemId] = useState('');
  const [resourcePickerItemId, setResourcePickerItemId] = useState('');
  const [linkModalItemId, setLinkModalItemId] = useState('');
  const [pendingAddLinkItemId, setPendingAddLinkItemId] = useState('');
  const [pendingSetField, setPendingSetField] = useState(null);
  const canReorder = checklistItems.length > 1;
  const actionMenuRefs = useRef(new Map());

  const getActionMenuRef = useCallback((itemId) => {
    const key = String(itemId || '').trim();
    if (!key) return { current: null };
    if (!actionMenuRefs.current.has(key)) {
      actionMenuRefs.current.set(key, { current: null });
    }
    return actionMenuRefs.current.get(key);
  }, []);

  useEffect(() => {
    setItemLabelDrafts((prev) => {
      const next = {};
      checklistItems.forEach((item) => {
        const itemId = item?.id || '';
        if (!itemId) return;
        if (Object.prototype.hasOwnProperty.call(prev, itemId)) {
          next[itemId] = prev[itemId];
        }
      });
      return next;
    });
  }, [checklistItems]);

  const emit = useCallback((nextItems, nextRequired = required) => {
    onChange?.({
      items: normalizeEditorChecklistItems(nextItems),
      required: nextRequired === true,
    });
  }, [onChange, required]);

  const uniqueKey = useCallback((desired, existingKeys) => {
    let candidate = desired || 'item';
    if (!existingKeys.has(candidate)) return candidate;
    let counter = 2;
    while (existingKeys.has(`${candidate}_${counter}`)) counter += 1;
    return `${candidate}_${counter}`;
  }, []);

  const handleAdd = useCallback(() => {
    const existingKeys = new Set(checklistItems.map((item) => item.key));
    const key = uniqueKey(generateEnumOptionValue('Item'), existingKeys);
    const id = createEnumOptionId();
    setItemLabelDrafts((prev) => ({
      ...prev,
      [id]: '',
    }));
    emit([
      ...checklistItems,
      {
        id,
        key,
        translations: { en: { label: '' } },
        sortOrder: checklistItems.length,
        actions: [],
        resources: [],
      },
    ]);
  }, [checklistItems, emit, uniqueKey]);

  const handleRemove = useCallback((itemId) => {
    emit(checklistItems.filter((item) => item.id !== itemId));
  }, [checklistItems, emit]);

  const handleLabelCommit = useCallback((itemId, rawLabel) => {
    const label = String(rawLabel || '').trim();
    emit(checklistItems.map((item) => {
      if (item.id !== itemId) return item;
      return {
        ...item,
        translations: { en: { label } },
      };
    }));
    setItemLabelDrafts((prev) => {
      if (!Object.prototype.hasOwnProperty.call(prev, itemId)) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, [checklistItems, emit]);

  const handleRequestSetFieldAction = useCallback((itemId, field) => {
    const fieldKey = String(field?.key || '').trim();
    const targetItemId = String(itemId || '').trim();
    if (!targetItemId || !fieldKey) return;
    setPendingSetField({ itemId: targetItemId, field });
  }, []);

  const handleConfirmSetFieldAction = useCallback(({ field, requireValue }) => {
    const itemId = String(pendingSetField?.itemId || '').trim();
    const fieldKey = String(field?.key || '').trim();
    if (!itemId || !fieldKey) return;

    emit(checklistItems.map((item) => {
      if (item.id !== itemId) return item;
      const existing = normalizeChecklistItemActions(item.actions);
      if (existing.some((action) => action.fieldKey === fieldKey)) return item;
      const nextAction = buildSetFieldChecklistAction({
        fieldKey,
        fieldLabel: field.label,
        requireValue: requireValue === true,
      });
      if (!nextAction) return item;
      return {
        ...item,
        actions: [...existing, nextAction],
      };
    }));
    setPendingSetField(null);
  }, [checklistItems, emit, pendingSetField?.itemId]);

  const handleAddTaskAttachAction = useCallback((itemId, actionType) => {
    const type = String(actionType || '').trim();
    const targetItemId = String(itemId || '').trim();
    if (!targetItemId || !type) return;

    if (type === CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK) {
      const existing = normalizeChecklistItemActions(
        checklistItems.find((item) => item.id === targetItemId)?.actions,
      );
      if (existing.some((action) => action.type === type)) return;
      setPendingAddLinkItemId(targetItemId);
      return;
    }

    emit(checklistItems.map((item) => {
      if (item.id !== targetItemId) return item;
      const existing = normalizeChecklistItemActions(item.actions);
      if (existing.some((action) => action.type === type)) return item;
      const nextAction = buildTaskAttachChecklistAction({ type });
      if (!nextAction) return item;
      return {
        ...item,
        actions: [...existing, nextAction],
      };
    }));
  }, [checklistItems, emit]);

  const handleConfirmAddLinkAction = useCallback(({ label, defaultTitle }) => {
    const itemId = String(pendingAddLinkItemId || '').trim();
    if (!itemId) return;

    emit(checklistItems.map((item) => {
      if (item.id !== itemId) return item;
      const existing = normalizeChecklistItemActions(item.actions);
      if (existing.some((action) => action.type === CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK)) {
        return item;
      }
      const nextAction = buildTaskAttachChecklistAction({
        type: CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK,
        label,
        defaultTitle,
      });
      if (!nextAction) return item;
      return {
        ...item,
        actions: [...existing, nextAction],
      };
    }));
    setPendingAddLinkItemId('');
  }, [checklistItems, emit, pendingAddLinkItemId]);

  const handleRemoveAction = useCallback((itemId, actionId) => {
    emit(checklistItems.map((item) => {
      if (item.id !== itemId) return item;
      return {
        ...item,
        actions: normalizeChecklistItemActions(item.actions)
          .filter((action) => action.id !== actionId),
      };
    }));
  }, [checklistItems, emit]);

  const handleAddResourceNode = useCallback((itemId, node) => {
    const targetItemId = String(itemId || '').trim();
    const nodeKey = String(node?.key || '').trim();
    if (!targetItemId || !nodeKey) return;

    const typeKey = String(node?.type || '').trim();
    if (taskRelatedTypeKeys && typeKey && !taskRelatedTypeKeys.has(typeKey)) {
      toast.info('That type cannot be attached to a task.');
      return;
    }

    emit(checklistItems.map((item) => {
      if (item.id !== targetItemId) return item;
      const existing = normalizeChecklistItemResources(item.resources);
      if (existing.some((resource) => resource.nodeKey === nodeKey)) return item;
      const nextResource = buildChecklistItemResource({
        nodeKey,
        title: node?.title,
      });
      if (!nextResource) return item;
      return {
        ...item,
        resources: [...existing, nextResource],
      };
    }));
  }, [checklistItems, emit, taskRelatedTypeKeys]);

  const handleRemoveResource = useCallback((itemId, resourceId) => {
    emit(checklistItems.map((item) => {
      if (item.id !== itemId) return item;
      return {
        ...item,
        resources: normalizeChecklistItemResources(item.resources)
          .filter((resource) => resource.id !== resourceId),
      };
    }));
  }, [checklistItems, emit]);

  const handleDragStart = useCallback((itemId, event) => {
    const nextItemId = String(itemId || '').trim();
    if (!canReorder || !nextItemId) return;
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', nextItemId);
    setDraggingItemId(nextItemId);
  }, [canReorder]);

  const handleDragOver = useCallback((itemId, event) => {
    const nextItemId = String(itemId || '').trim();
    if (!draggingItemId || !nextItemId || draggingItemId === nextItemId) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverItemId(nextItemId);
  }, [draggingItemId]);

  const handleDragEnd = useCallback(() => {
    setDraggingItemId('');
    setDragOverItemId('');
  }, []);

  const handleDrop = useCallback((itemId, event) => {
    const targetItemId = String(itemId || '').trim();
    if (!draggingItemId || !targetItemId || draggingItemId === targetItemId) {
      handleDragEnd();
      return;
    }

    event.preventDefault();

    const draggedIndex = checklistItems.findIndex((item) => item.id === draggingItemId);
    const targetIndex = checklistItems.findIndex((item) => item.id === targetItemId);
    if (draggedIndex < 0 || targetIndex < 0) {
      handleDragEnd();
      return;
    }

    const next = [...checklistItems];
    const [moved] = next.splice(draggedIndex, 1);
    next.splice(targetIndex, 0, moved);
    emit(next.map((item, sortOrder) => ({ ...item, sortOrder })));
    handleDragEnd();
  }, [checklistItems, draggingItemId, emit, handleDragEnd]);

  return (
    <ModalField>
      <div className="mission-task-checklist-editor-required">
        <Checkbox
          label="Required - must be completed before marking the task complete."
          size="16"
          value={required === true}
          onChange={() => emit(checklistItems, !(required === true))}
        />
      </div>

      {checklistItems.length === 0 ? (
        <div
          className="mission-task-modal-empty"
          style={{ color: theme?.foregroundMuted }}
        >
          No checklist items yet.
        </div>
      ) : (
        <div className="mission-task-checklist-editor-list">
          {checklistItems.map((item) => {
            const itemId = item.id;
            const itemLabel = getEditorItemLabel(item);
            const hasDraft = Object.prototype.hasOwnProperty.call(itemLabelDrafts, itemId);
            const draftLabel = hasDraft ? itemLabelDrafts[itemId] : itemLabel;
            const isDragging = draggingItemId === itemId;
            const isDropTarget = dragOverItemId === itemId;
            const itemActions = normalizeChecklistItemActions(item.actions);
            const itemResources = normalizeChecklistItemResources(item.resources);
            const usedFieldKeys = new Set(
              itemActions
                .map((action) => String(action.fieldKey || '').trim())
                .filter(Boolean),
            );
            const usedMediaTypes = new Set(
              itemActions.map((action) => String(action.type || '').trim()),
            );
            const actionMenuTriggerRef = getActionMenuRef(itemId);

            return (
              <div
                className={[
                  'mission-task-checklist-editor-row',
                  canReorder ? 'is-sortable' : '',
                  isDragging ? 'is-dragging' : '',
                  isDropTarget ? 'is-drag-over' : '',
                ].filter(Boolean).join(' ')}
                key={itemId}
                style={{
                  backgroundColor: isDropTarget ? theme?.backgroundHover : 'transparent',
                }}
                onDragOver={(event) => handleDragOver(itemId, event)}
                onDrop={(event) => handleDrop(itemId, event)}
              >
                <div className="mission-task-checklist-editor-row-main">
                  <div
                    aria-label="Drag to reorder"
                    className="mission-task-checklist-editor-drag"
                    draggable={canReorder}
                    title={canReorder ? 'Drag to reorder' : 'Add more items to reorder'}
                    onDragEnd={handleDragEnd}
                    onDragStart={(event) => handleDragStart(itemId, event)}
                  >
                    <Icon
                      color={canReorder ? theme?.foregroundMuted : theme?.backgroundMuted}
                      height={16}
                      iconKey="drag_indicator"
                      width={16}
                    />
                  </div>

                  <TextInput
                    expandable
                    multiline
                    placeholder="Checklist item"
                    value={draftLabel}
                    onBlur={() => handleLabelCommit(itemId, draftLabel)}
                    onChange={(event) => {
                      const nextLabel = event.target.value;
                      setItemLabelDrafts((prev) => ({
                        ...prev,
                        [itemId]: nextLabel,
                      }));
                    }}
                  />

                  <span
                    ref={(element) => {
                      actionMenuTriggerRef.current = element;
                    }}
                    className="mission-task-checklist-editor-action-trigger"
                  >
                    <button
                      aria-label="Add checklist action"
                      className="mission-task-checklist-editor-action-button"
                      title="Add action"
                      type="button"
                    >
                      <Icon
                        color={theme?.foregroundMuted}
                        height={18}
                        iconKey="bolt"
                        width={18}
                      />
                    </button>
                  </span>

                  <ContextMenu
                    triggerMode="click"
                    triggerRef={actionMenuTriggerRef}
                    zIndex={1400}
                  >
                    <ContextMenu.Item
                      iconKey="edit"
                      label="Set Field Value"
                    >
                      {modalFields.length > 0 ? (
                        modalFields.map((field) => (
                          <ContextMenu.Item
                            disabled={usedFieldKeys.has(field.key)}
                            key={field.key}
                            label={field.label}
                            onClick={() => handleRequestSetFieldAction(itemId, field)}
                          />
                        ))
                      ) : (
                        <ContextMenu.Item
                          disabled
                          label="No supported fields"
                        />
                      )}
                    </ContextMenu.Item>
                    <ContextMenu.Separator />
                    <ContextMenu.Section label="Action buttons" />
                    {actionButtonOptions.map((option) => {
                      const isLinkAction = option.type === CHECKLIST_ITEM_ACTION_TYPES.ADD_LINK;
                      return (
                        <ContextMenu.Item
                          disabled={
                            usedMediaTypes.has(option.type)
                            || (isLinkAction && !canUseLinkType)
                          }
                          iconKey={option.iconKey}
                          key={option.type}
                          label={option.label}
                          onClick={() => handleAddTaskAttachAction(itemId, option.type)}
                        />
                      );
                    })}
                    <ContextMenu.Separator />
                    <ContextMenu.Section label="Resource buttons" />
                    <ContextMenu.Item
                      iconKey="search"
                      label="Existing Item"
                      onClick={() => setResourcePickerItemId(itemId)}
                    />
                    <ContextMenu.Item
                      disabled={!canUseLinkType}
                      iconKey="link"
                      label="Resource Link"
                      onClick={() => setLinkModalItemId(itemId)}
                    />
                  </ContextMenu>

                  <button
                    aria-label="Remove item"
                    className="mission-task-checklist-editor-remove"
                    title="Remove item"
                    type="button"
                    onClick={() => handleRemove(itemId)}
                  >
                    <Icon
                      color={theme?.foregroundMuted}
                      height={18}
                      iconKey="do_not_disturb_on"
                      width={18}
                    />
                  </button>
                </div>

                {itemActions.length > 0 || itemResources.length > 0 ? (
                  <div className="mission-task-checklist-editor-actions">
                    {itemActions.map((action) => (
                      <div
                        className="mission-task-checklist-editor-action-chip"
                        key={action.id}
                      >
                        <Button
                          backgroundColor="transparent"
                          borderColor="transparent"
                          fontSize="12px"
                          foregroundColor={theme?.foreground}
                          hoverForegroundColor={theme?.foreground}
                          height="28px"
                          hoverBackgroundColor={theme?.backgroundHover}
                          iconKey={getChecklistActionIconKey(action)}
                          label={[
                            getChecklistActionButtonLabel(action, modalFields),
                            action.requireValue === true ? 'Required' : '',
                          ].filter(Boolean).join(' · ')}
                          padding="4px 8px"
                        />
                        <button
                          aria-label="Remove action"
                          className="mission-task-checklist-editor-action-remove"
                          title="Remove action"
                          type="button"
                          onClick={() => handleRemoveAction(itemId, action.id)}
                        >
                          <Icon
                            color={theme?.foregroundMuted}
                            height={16}
                            iconKey="close"
                            width={16}
                          />
                        </button>
                      </div>
                    ))}
                    {itemResources.map((resource) => (
                      <div
                        className="mission-task-checklist-editor-action-chip"
                        key={resource.id}
                      >
                        <Button
                          backgroundColor="transparent"
                          borderColor="transparent"
                          fontSize="12px"
                          foregroundColor={theme?.foreground}
                          hoverForegroundColor={theme?.foreground}
                          height="28px"
                          hoverBackgroundColor={theme?.backgroundHover}
                          iconKey="bookmark"
                          label={resource.title || 'Resource'}
                          padding="4px 8px"
                        />
                        <button
                          aria-label="Remove resource"
                          className="mission-task-checklist-editor-action-remove"
                          title="Remove resource"
                          type="button"
                          onClick={() => handleRemoveResource(itemId, resource.id)}
                        >
                          <Icon
                            color={theme?.foregroundMuted}
                            height={16}
                            iconKey="close"
                            width={16}
                          />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <div className="mission-task-modal-inline-actions">
        <Button
          iconKey="add"
          label="Add Item"
          onClick={handleAdd}
        />
      </div>

      {error ? (
        <div className="mission-task-modal-error">
          {error}
        </div>
      ) : null}

      <AddModal
        insertMode
        isOpen={Boolean(resourcePickerItemId)}
        node={null}
        title="Add resource"
        onClose={() => setResourcePickerItemId('')}
        onExistingSelected={(node) => {
          handleAddResourceNode(resourcePickerItemId, node);
          setResourcePickerItemId('');
        }}
        onTypeSelect={() => {}}
      />

      <MissionTaskChecklistLinkModal
        isOpen={Boolean(linkModalItemId)}
        setOpen={(open) => {
          if (!open) setLinkModalItemId('');
        }}
        onCreated={(node) => {
          handleAddResourceNode(linkModalItemId, node);
          setLinkModalItemId('');
        }}
      />

      <MissionTaskChecklistSetFieldModal
        field={pendingSetField?.field || null}
        isOpen={Boolean(pendingSetField?.itemId && pendingSetField?.field?.key)}
        setOpen={(open) => {
          if (!open) setPendingSetField(null);
        }}
        onConfirm={handleConfirmSetFieldAction}
      />

      <MissionTaskChecklistAddLinkModal
        isOpen={Boolean(pendingAddLinkItemId)}
        setOpen={(open) => {
          if (!open) setPendingAddLinkItemId('');
        }}
        onConfirm={handleConfirmAddLinkAction}
      />
    </ModalField>
  );
});

MissionTaskChecklistEditor.displayName = 'MissionTaskChecklistEditor';

export default MissionTaskChecklistEditor;
